import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { AlertCircle, CheckCircle2, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface BootstrapResult {
  firmId: string;
  firmName: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
}

export function BootstrapSubscriptions({ accessToken }: { accessToken: string }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BootstrapResult[]>([]);

  const bootstrapAllFirms = async () => {
    setLoading(true);
    setResults([]);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/bootstrap-subscriptions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Bootstrap failed');
      }

      const data = await response.json();
      setResults(data.results || []);
      
      const successCount = data.results.filter((r: BootstrapResult) => r.status === 'success').length;
      const errorCount = data.results.filter((r: BootstrapResult) => r.status === 'error').length;
      const skippedCount = data.results.filter((r: BootstrapResult) => r.status === 'skipped').length;

      if (successCount > 0) {
        toast.success(`Bootstrapped ${successCount} firm(s) successfully`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to bootstrap ${errorCount} firm(s)`);
      }
      if (skippedCount > 0) {
        toast.info(`Skipped ${skippedCount} firm(s) (already initialized)`);
      }
    } catch (error: any) {
      console.error('Bootstrap error:', error);
      toast.error(error.message || 'Failed to bootstrap subscriptions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Bootstrap Subscriptions
        </CardTitle>
        <CardDescription>
          Initialize subscription data for manually-created consulting firms
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Testing/Migration Utility:</strong> This initializes firms that were created manually (outside the normal signup flow)
            by setting them to the Free tier with 14-day trial.
            <br /><br />
            <strong>Note:</strong> For production, use the normal signup flow. See <code>/docs/DECISION_GUIDE.md</code> for guidance.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            This will:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>Set all firms without subscriptions to "Free" tier</li>
            <li>Enable 14-day trials starting today</li>
            <li>Set subscription status to "trialing"</li>
            <li>Skip firms that already have subscription data</li>
            <li><strong>System Admin data is protected</strong> (only modifies firm records)</li>
          </ul>
        </div>

        <Button 
          onClick={bootstrapAllFirms} 
          disabled={loading}
          className="w-full"
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Bootstrap All Firms
        </Button>

        {results.length > 0 && (
          <div className="space-y-2 mt-4">
            <h3 className="font-semibold">Results:</h3>
            <div className="space-y-2">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-2 p-3 border rounded-lg"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {result.status === 'success' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                    {result.status === 'error' && <AlertCircle className="h-4 w-4 text-red-600" />}
                    {result.status === 'skipped' && <AlertCircle className="h-4 w-4 text-yellow-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{result.firmName}</span>
                      <Badge 
                        variant={
                          result.status === 'success' ? 'default' : 
                          result.status === 'error' ? 'destructive' : 
                          'secondary'
                        }
                      >
                        {result.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{result.message}</p>
                    <p className="text-xs text-muted-foreground font-mono">{result.firmId}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
